/**
 * HpClauseRow  (SM-22 / DA-2295; column + value sourced from values[].tag — DA-2297)
 *
 * The HP cascade: attribute (catalog) → {D, C} operator (READ only — write has no operator
 * letter, §5) → value.
 *
 * CRITICAL: the mapper column and value code are NOT the catalog's display ids. Each catalog
 * value carries a `tag` = `HP:<column>:<op>:<code>` (attribute "Property Type"/`propertyType`,
 * value "Detached"/`detached` → `HP:dwelling:D:6`). Every option here is keyed off the tag —
 * the attribute picker stores the COLUMN (`dwelling`) as `attributePath`, and the discrete
 * value picker stores the CODE (`6`) as `value` — so serialization yields `HP:dwelling:D:6`
 * (read) / `HP:dwelling:6` (write), never `HP:propertyType:D:detached`. See ./hpTag.ts.
 *
 * The operator set is narrowed to what the attribute's tags actually use, so a purely
 * DISCRETE column never offers C and a purely RANGE column never offers D. The value
 * position's shape follows the operator:
 *   - READ + D → single-value SearchableSelect over the catalog codes. A survey choice maps to
 *     ONE value (it must write one concrete code), so it is NOT the grammar's `-`-joined set.
 *   - READ + C → RangeValueEditor (ranges are per-question, author-entered)
 *   - WRITE/UNSELECT → one concrete value: a SearchableSelect of catalog codes when the
 *     column is discrete, else a plain field (RANGE columns write an author-entered scalar —
 *     e.g. read `HP:numOccupants:C:0-10` → write `HP:numOccupants:1`).
 */
import React from 'react'
import { Box, Checkbox, FormControlLabel, IconButton, TextField, Typography } from '@mui/material'
import { Trash2 } from 'lucide-react'
import { SearchableSelect } from 'components/common/SearchableSelect'
import { HpMapperClause, MapperCatalog, MapperRole } from 'types/apis/surveyInterfaces'
import { CascadeRow } from './CascadeRow'
import { CascadeStepDef, CascadeValues, MapperScopeConstraint } from './cascadeTypes'
import { RangeValueEditor } from './RangeValueEditor'
import { columnDiscreteOptions, columnOperators, hpColumnOptions } from './hpTag'

export interface HpClauseRowProps {
  clause: HpMapperClause
  role: MapperRole
  catalog?: MapperCatalog
  loading?: boolean
  /** Forward-guard (DA-2184): pins the attribute TARGET when `scope.attributePath` is set. */
  scope?: MapperScopeConstraint
  onChange: (clause: HpMapperClause) => void
  onRemove: () => void
}

function readOperatorOptions(catalog: MapperCatalog | undefined, ops: Set<'D' | 'C'>) {
  const all = catalog?.rules.hp.readOperators ?? []
  const allowed = ops.size ? all.filter((o) => ops.has(o.code as 'D' | 'C')) : all
  return allowed.map((o) => ({ value: o.code, label: o.label }))
}

export const HpClauseRow: React.FC<HpClauseRowProps> = ({ clause, role, catalog, loading, scope, onChange, onRemove }) => {
  const isRead = role === 'READ'
  const attributes = catalog?.homeProfileAttributes
  // Forward-guard: a scoped attribute pins the target column for every clause; operator +
  // value stay free. Falls back to the clause's own path when there is no scope.
  const scopedColumn = scope?.attributePath
  const column = scopedColumn ?? clause.attributePath ?? ''

  // Everything is keyed off the mapper column (from the tag), aggregated across any attributes
  // that share it, so `value`/`operator` never come from a display id.
  const valueOptions = columnDiscreteOptions(attributes, column)
  const ops = columnOperators(attributes, column)
  // A column is range-capable if ANY attribute mapping to it uses C — then a write is an
  // author-entered scalar (real: `HP:numOccupants:C:0-10` read → `HP:numOccupants:1` write),
  // never one of the discrete codes (which, for a shared column, belong to the other account
  // type's attribute). A purely discrete column writes a catalog code picked from the tag.
  const rangeCapable = ops.has('C')
  const writeUsesScalar = rangeCapable || valueOptions.length === 0

  const columnOptions = hpColumnOptions(attributes)
  const targetStep: CascadeStepDef = {
    key: 'target',
    label: 'Attribute',
    options: columnOptions,
    placeholder: loading ? 'Loading catalog…' : 'Select an attribute',
    ...(scopedColumn
      ? { locked: true, lockedLabel: columnOptions.find((o) => o.value === scopedColumn)?.label ?? scopedColumn }
      : {}),
  }

  const operatorStep: CascadeStepDef = {
    key: 'operator',
    label: 'Operator',
    options: readOperatorOptions(catalog, ops),
    placeholder: 'Select an operator',
  }

  const steps = isRead ? [targetStep, operatorStep] : [targetStep]

  const cascadeValues: CascadeValues = {
    target: column,
    ...(isRead ? { operator: clause.operator && clause.operator !== 'NONE' ? clause.operator : '' } : {}),
  }

  const handleCascadeChange = (values: CascadeValues) => {
    onChange({
      ...clause,
      attributePath: values.target ?? '',
      ...(isRead ? { operator: (values.operator as 'D' | 'C' | undefined) || undefined } : {}),
      // Attribute (and, for READ, operator) changed — the old value's shape no longer applies.
      value: undefined,
      rangeStart: undefined,
      rangeEnd: undefined,
    })
  }

  const renderValueControl = () => {
    if (clause.isNull) return null

    if (isRead) {
      if (clause.operator === 'D') {
        // DA-2184: a survey CHOICE maps to exactly ONE attribute value — the choice IS the
        // distinct answer, and when selected it must WRITE a single concrete code (a multi-value
        // read like `HP:dwelling:D:1-2-3` derives the malformed write `HP:dwelling:1-2-3`). So the
        // value picker is single-select here, not the grammar's general `-`-joined "in {…}" set.
        return (
          <SearchableSelect
            label="Value"
            value={clause.value ?? ''}
            options={valueOptions}
            onChange={(v) => onChange({ ...clause, value: v })}
          />
        )
      }
      if (clause.operator === 'C') {
        return (
          <RangeValueEditor
            value={{ value: clause.value, rangeStart: clause.rangeStart, rangeEnd: clause.rangeEnd }}
            onChange={(v) => onChange({ ...clause, ...v })}
          />
        )
      }
      return (
        <Typography variant="body2" color="text.disabled">
          Pick an operator to set a value
        </Typography>
      )
    }

    // WRITE / UNSELECT — one concrete value, never a set or range. Discrete columns write a
    // catalog code (auto-derived from the tag); range-capable columns write an author-entered scalar.
    if (writeUsesScalar) {
      return (
        <TextField
          label="Value"
          size="small"
          value={clause.value ?? ''}
          onChange={(e) => onChange({ ...clause, value: e.target.value })}
        />
      )
    }
    return (
      <SearchableSelect
        label="Value"
        value={clause.value ?? ''}
        onChange={(v) => onChange({ ...clause, value: v })}
        options={valueOptions}
      />
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <CascadeRow steps={steps} values={cascadeValues} onChange={handleCascadeChange} loading={loading} />
        </Box>
        <IconButton size="small" onClick={onRemove} aria-label="Remove read condition" sx={{ color: 'error.main' }}>
          <Trash2 size={16} />
        </IconButton>
      </Box>
      {!isRead && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={!!clause.isNull}
              onChange={(e) => onChange({ ...clause, isNull: e.target.checked, value: undefined })}
            />
          }
          label="Set to null (clear this attribute)"
        />
      )}
      {clause.attributePath && renderValueControl()}
    </Box>
  )
}
